import React, { useEffect, useState } from "react";
import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";
import { padLeadingZeroes } from "utils/utilityFunctions";
import { BASE_URL } from "utils/constant";

const styles = StyleSheet.create({
    page: {
        padding: 40,
        fontFamily: "Helvetica",
        fontSize: 10,
        color: "#333",
        backgroundColor: "#fff",
    },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 30,
        borderBottom: "2 solid #3B0069",
        paddingBottom: 15,
    },
    titleContainer: {
        flexDirection: "column",
    },
    title: {
        fontSize: 20,
        fontWeight: "bold",
        color: "#3B0069",
        letterSpacing: 1,
    },
    subtitle: {
        fontSize: 12,
        color: "#666",
        marginTop: 5,
    },
    orgDetails: {
        textAlign: "right",
        alignItems: "flex-end",
    },
    orgLogo: {
        width: 80,
        height: 60,
        marginBottom: 5,
        objectFit: "contain",
    },
    orgName: {
        fontSize: 14,
        fontWeight: "bold",
        color: "#3B0069",
        marginBottom: 4,
    },
    orgAddress: {
        fontSize: 9,
        color: "#666",
        maxWidth: 200,
        textAlign: "right",
        lineHeight: 1.4,
    },
    section: {
        marginBottom: 20,
    },
    sectionTitle: {
        fontSize: 12,
        fontWeight: "bold",
        backgroundColor: "#F3F4F6",
        padding: 6,
        color: "#3B0069",
        marginBottom: 10,
        textTransform: "uppercase",
    },
    infoGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
        marginBottom: 10,
    },
    infoItem: {
        width: "50%",
        marginBottom: 8,
        paddingRight: 10,
    },
    label: {
        fontSize: 9,
        color: "#666",
        marginBottom: 2,
        textTransform: "uppercase",
    },
    value: {
        fontSize: 10,
        fontWeight: "bold",
        color: "#1F2937",
    },
    table: {
        marginTop: 5,
        border: "1 solid #E5E7EB",
        borderRadius: 4,
    },
    tableHeader: {
        flexDirection: "row",
        backgroundColor: "#3B0069",
        padding: "8 10",
    },
    tableRow: {
        flexDirection: "row",
        borderBottom: "0.5 solid #E5E7EB",
        padding: "9 10",
        alignItems: "flex-start",
    },
    tableRowEven: {
        flexDirection: "row",
        borderBottom: "0.5 solid #E5E7EB",
        padding: "9 10",
        alignItems: "flex-start",
        backgroundColor: "#FAFAFA",
    },
    tableSubRow: {
        flexDirection: "row",
        padding: "5 10 5 24",
        alignItems: "center",
        backgroundColor: "#F9F5FF",
        borderBottom: "0.5 solid #EDE9F6",
    },
    sectionTotalRow: {
        flexDirection: "row",
        padding: "7 10",
        backgroundColor: "#EDE9F6",
        borderBottom: "1 solid #D8D0EE",
    },
    colSno: { width: "6%", textAlign: "center" },
    colName: { width: "38%" },
    colPrice: { width: "18%", textAlign: "right" },
    colTax: { width: "18%", textAlign: "right" },
    colTotal: { width: "20%", textAlign: "right" },

    tableHeaderText: {
        fontSize: 9,
        color: "#FFFFFF",
        fontWeight: "bold",
    },
    tableCell: {
        fontSize: 9,
        color: "#4B5563",
    },
    itemName: {
        fontSize: 9,
        fontWeight: "bold",
        color: "#1F2937",
    },
    subItemName: {
        fontSize: 8,
        color: "#6B7280",
    },
    sectionTotalLabel: {
        fontSize: 9,
        fontWeight: "bold",
        color: "#3B0069",
    },
    sectionTotalValue: {
        fontSize: 9,
        fontWeight: "bold",
        color: "#3B0069",
    },
    summarySection: {
        marginTop: 12,
        alignSelf: "flex-end",
        width: "40%",
        borderTop: "1 solid #E5E7EB",
        paddingTop: 8,
    },
    summaryRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginBottom: 4,
    },
    summaryLabel: {
        fontSize: 10,
        color: "#666",
    },
    summaryValue: {
        fontSize: 10,
        fontWeight: "bold",
        color: "#333",
    },
    grandTotalRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        borderTop: "1.5 solid #3B0069",
        paddingTop: 8,
        marginTop: 4,
    },
    grandTotalLabel: {
        fontSize: 12,
        fontWeight: "bold",
        color: "#3B0069",
    },
    grandTotalValue: {
        fontSize: 12,
        fontWeight: "bold",
        color: "#3B0069",
    },
    termsContainer: {
        marginTop: 30,
        padding: 10,
        backgroundColor: "#F9FAFB",
        borderRadius: 4,
    },
    termsTitle: {
        fontSize: 10,
        fontWeight: "bold",
        marginBottom: 5,
        color: "#374151",
    },
    termsText: {
        fontSize: 8,
        color: "#6B7280",
        lineHeight: 1.5,
    },
    footer: {
        position: "absolute",
        bottom: 30,
        left: 40,
        right: 40,
        textAlign: "center",
        borderTop: "0.5 solid #E5E7EB",
        paddingTop: 10,
        fontSize: 8,
        color: "#999",
    }
});

const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return "-";
    return `Rs. ${Number(amount).toLocaleString("en-IN")}`;
};

const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
        return new Date(dateStr).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch (e) {
        return dateStr;
    }
};

export function QuotationPDFDocument({ quotation, client, organization, customCharges, currentOrganization }) {
    // const [currentOrganization, setCurrentOrganization] = useState(null);
    // const fetchOrganizationDetails = async () => {
    //     const response = await fetch(`${BASE_URL}/org/get-a-organization/${organization.id}/`,
    //         {
    //             method: "GET",
    //             headers: {
    //                 "Content-Type": "application/json",
    //                 "Authorization": `Bearer ${localStorage.getItem("access")}`,
    //             },
    //         }
    //     )
    //     const data = await response.json();
    //     setCurrentOrganization(data.data);
    // }
    // useEffect(() => {
    //     fetchOrganizationDetails();
    // }, [])

    if (!quotation) return null;

    const items = quotation.items || [];

    const documentTitle = `Quotation_${quotation?.id || "preview"}.pdf`;

    return (
        <Document title={documentTitle}>
            <Page size="A4" style={styles.page}>
                {/* Header Section */}
                <View style={styles.header}>
                    <View style={styles.titleContainer}>
                        {currentOrganization?.logo_image_file ? (
                            <Image src={currentOrganization.logo_image_file} style={styles.orgLogo} />
                        ) : null}
                        <Text style={styles.title}>QUOTATION</Text>
                    </View>
                    <View style={styles.orgDetails}>
                        <Text style={styles.orgName}>{organization?.organization_name || "Organization Name"}</Text>
                        <Text style={styles.orgAddress}>{organization?.business_address || ""}</Text>
                        <Text style={styles.orgAddress}>
                            {organization?.state ? `${organization.state}` : ""}
                            {organization?.pincode ? `, ${organization.pincode}` : ""}
                        </Text>
                        {organization?.user_email && (
                            <Text style={styles.orgAddress}>{organization.user_email}</Text>
                        )}
                        {organization?.website && (
                            <Text style={styles.orgAddress}>{organization.website}</Text>
                        )}
                        <Text style={[styles.subtitle, { fontSize: 10 }]}>
                            Date: {formatDate(new Date().toISOString())}
                        </Text>
                    </View>
                </View>

                {/* Client & Project Info */}
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Client Details</Text>
                    <View style={styles.infoGrid}>
                        <View style={styles.infoItem}>
                            <Text style={styles.label}>Client Name</Text>
                            <Text style={styles.value}>{client?.name || "-"}</Text>
                        </View>
                        <View style={styles.infoItem}>
                            <Text style={styles.label}>Email</Text>
                            <Text style={styles.value}>{client?.email || "-"}</Text>
                        </View>
                        <View style={styles.infoItem}>
                            <Text style={styles.label}>Phone</Text>
                            <Text style={styles.value}>{client?.phone || "-"}</Text>
                        </View>
                        <View style={styles.infoItem}>
                            <Text style={styles.label}>Site Location</Text>
                            <Text style={styles.value}>{client?.city || "-"}</Text>
                        </View>
                        <View style={styles.infoItem}>
                            <Text style={styles.label}>Project Type</Text>
                            <Text style={styles.value}>{client?.projectType || "-"}</Text>
                        </View>
                    </View>
                </View>

                {/* Items & Subitems Table */}
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Quotation Items</Text>
                    <View style={styles.table}>
                        {/* Table Header */}
                        <View style={styles.tableHeader}>
                            <Text style={[styles.colSno, styles.tableHeaderText]}>#</Text>
                            <Text style={[styles.colName, styles.tableHeaderText]}>Item / Description</Text>
                            <Text style={[styles.colPrice, styles.tableHeaderText]}>Price</Text>
                            <Text style={[styles.colTax, styles.tableHeaderText]}>Tax</Text>
                            <Text style={[styles.colTotal, styles.tableHeaderText]}>Total</Text>
                        </View>

                        {items.length === 0 && (
                            <View style={[styles.tableRow, { justifyContent: "center", padding: 20 }]}>
                                <Text style={{ fontSize: 10, color: "#999" }}>No items added</Text>
                            </View>
                        )}

                        {items.map((item, index) => {
                            const itemPrice = Number(item.price || 0);
                            const itemTax = Number(item.tax || 0);
                            const itemTotal = itemPrice + itemTax;
                            const subItems = item.subItems || item.subitems || [];
                            const hasSubItems = subItems.length > 0;

                            const subItemsPrice = subItems.reduce((sum, s) => sum + Number(s.price || 0), 0);
                            const subItemsTax = subItems.reduce((sum, s) => sum + Number(s.tax || 0), 0);

                            const sectionPriceTotal = itemPrice + subItemsPrice;
                            const sectionTaxTotal = itemTax + subItemsTax;
                            const sectionGrandTotal = sectionPriceTotal + sectionTaxTotal;

                            const rowStyle = index % 2 === 0 ? styles.tableRow : styles.tableRowEven;

                            return (
                                <React.Fragment key={item.id || index}>
                                    {/* Parent Item Row */}
                                    <View style={rowStyle}>
                                        <Text style={[styles.colSno, styles.tableCell, { color: "#3B0069", fontWeight: "bold" }]}>
                                            {index + 1}
                                        </Text>
                                        <Text style={[styles.colName, styles.itemName]}>{item.name}</Text>
                                        <Text style={[styles.colPrice, styles.tableCell]}>
                                            {itemPrice > 0 ? formatCurrency(itemPrice) : "-"}
                                        </Text>
                                        <Text style={[styles.colTax, styles.tableCell]}>
                                            {itemTax > 0 ? formatCurrency(itemTax) : "-"}
                                        </Text>
                                        <Text style={[styles.colTotal, styles.tableCell, { fontWeight: "bold", color: "#1F2937" }]}>
                                            {itemTotal > 0 ? formatCurrency(itemTotal) : "-"}
                                        </Text>
                                    </View>

                                    {/* Subitems */}
                                    {subItems.map((sub, sIndex) => {
                                        const subPrice = Number(sub.price || 0);
                                        const subTax = Number(sub.tax || 0);
                                        const subTotal = subPrice + subTax;
                                        return (
                                            <View key={sub.id || `${index}-${sIndex}`} style={styles.tableSubRow}>
                                                <Text style={[styles.colSno, styles.subItemName, { textAlign: "center" }]}>
                                                    {index + 1}.{sIndex + 1}
                                                </Text>
                                                <Text style={[styles.colName, styles.subItemName]}>{sub.name}</Text>
                                                <Text style={[styles.colPrice, styles.subItemName]}>{formatCurrency(subPrice)}</Text>
                                                <Text style={[styles.colTax, styles.subItemName]}>{formatCurrency(subTax)}</Text>
                                                <Text style={[styles.colTotal, styles.subItemName]}>{formatCurrency(subTotal)}</Text>
                                            </View>
                                        );
                                    })}

                                    {/* Section Total Row — only shown when subitems exist */}
                                    {hasSubItems && (
                                        <View style={styles.sectionTotalRow}>
                                            <Text style={[styles.colSno, styles.sectionTotalLabel]}></Text>
                                            <Text style={[styles.colName, styles.sectionTotalLabel, { textAlign: "right" }]}>
                                                Section Total
                                            </Text>
                                            <Text style={[styles.colPrice, styles.sectionTotalValue]}>{formatCurrency(sectionPriceTotal)}</Text>
                                            <Text style={[styles.colTax, styles.sectionTotalValue]}>{formatCurrency(sectionTaxTotal)}</Text>
                                            <Text style={[styles.colTotal, styles.sectionTotalValue]}>{formatCurrency(sectionGrandTotal)}</Text>
                                        </View>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </View>

                    {/* Summary */}
                    <View style={styles.summarySection}>
                        <View style={styles.summaryRow}>
                            <Text style={styles.summaryLabel}>Subtotal</Text>
                            <Text style={styles.summaryValue}>{formatCurrency(quotation.subtotal)}</Text>
                        </View>
                        <View style={styles.summaryRow}>
                            <Text style={styles.summaryLabel}>Total Tax</Text>
                            <Text style={styles.summaryValue}>{formatCurrency(quotation.totalTax)}</Text>
                        </View>

                        {(customCharges || []).map((charge) => (
                            <View key={charge.id} style={styles.summaryRow}>
                                <Text style={styles.summaryLabel}>{charge.label}</Text>
                                <Text style={styles.summaryValue}>{formatCurrency(charge.amount)}</Text>
                            </View>
                        ))}

                        <View style={styles.grandTotalRow}>
                            <Text style={styles.grandTotalLabel}>Grand Total</Text>
                            <Text style={styles.grandTotalValue}>
                                {formatCurrency(
                                    Number(quotation.grandTotal || 0) +
                                    (customCharges || []).reduce((sum, c) => sum + Number(c.amount || 0), 0)
                                )}
                            </Text>
                        </View>
                    </View>
                </View>

                {/* Terms and Conditions */}
                <View style={styles.termsContainer}>
                    {quotation.paymentTerms ? (
                        <>
                            <Text style={styles.termsTitle}>Payment Terms</Text>
                            <Text style={[styles.termsText, { marginBottom: 10 }]}>{quotation.paymentTerms}</Text>
                        </>
                    ) : null}

                    {quotation.termsConditions ? (
                        <>
                            <Text style={styles.termsTitle}>Terms & Conditions</Text>
                            <Text style={styles.termsText}>{quotation.termsConditions}</Text>
                        </>
                    ) : (
                        <>
                            <Text style={styles.termsTitle}>Terms & Conditions</Text>
                            <Text style={styles.termsText}>Standard terms and conditions apply unless specified otherwise.</Text>
                        </>
                    )}
                </View>

                {/* Footer */}
                <View style={styles.footer}>
                    <Text>This is a computer-generated quotation generated via SiteMaster</Text>
                </View>
            </Page>
        </Document>
    );
}